import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import PublicTrustFooter from "@/components/PublicTrustFooter";

/**
 * Privacy — a plain-English privacy statement. Public route: no auth required
 * so anyone considering signing up can read it first. Deliberately short and
 * conversational (not lawyer-ese) because tradies won't read 12 pages of legal
 * before saving a docket, and we'd rather they trust us with a docket than
 * ignore a wall of clauses. Refresh this whenever the data model materially
 * changes.
 */
export default function Privacy() {
  const nav = useNavigate();
  const { user } = useAuth();

  return (
    <div className="min-h-screen bi-grain bi-ambient text-white">
      <div className="max-w-[720px] mx-auto px-5 py-10 space-y-6" data-testid="privacy-page">
        <button
          type="button"
          onClick={() => nav(-1)}
          data-testid="privacy-back"
          className="inline-flex items-center gap-1 text-white/60 hover:text-white text-sm"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <header className="space-y-2">
          <div className="inline-flex items-center gap-2 h-8 px-3 rounded-full bg-[#FF9500]/15 text-[#FF9500] text-[10px] uppercase tracking-[0.2em] font-heading font-bold">
            <ShieldCheck className="w-3.5 h-3.5" />
            Privacy
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight leading-tight">
            What we store — and what we don&apos;t.
          </h1>
          <p className="text-sm text-white/60">
            Plain English. No lawyer-speak. Last updated 15 Feb 2026.
          </p>
        </header>

        <section
          className="bi-surface p-5 sm:p-6 space-y-5 text-[15px] leading-relaxed text-white/85"
          data-testid="privacy-body"
        >
          <div className="space-y-2">
            <h2 className="font-heading text-lg font-extrabold text-white">
              What we store
            </h2>
            <p>
              BuildIntel AI keeps only the things it needs to help you log
              expenses and prep for BAS:
            </p>
            <ul className="list-disc pl-5 space-y-1.5 text-white/80">
              <li>Your account details — email, name, and (if you set them) business name, ABN, trade type, address, phone.</li>
              <li>Receipts and expense entries — supplier, total, GST, date, description, notes, job, category.</li>
              <li>Photos of receipts and voice recordings you attach to an entry.</li>
              <li>Job records — name, client, budget, status, and any site documents you upload.</li>
              <li>Automatically-generated summaries (BAS drafts, TPAR, weekly narrative) derived from the above.</li>
              <li>Basic activity such as when you logged in, so we can protect the account against brute-force attempts.</li>
            </ul>
          </div>

          <div className="space-y-2">
            <h2 className="font-heading text-lg font-extrabold text-white">
              What we don&apos;t do
            </h2>
            <ul className="list-disc pl-5 space-y-1.5 text-white/80">
              <li><b>We do not sell your data.</b> Full stop.</li>
              <li>We do not share it with advertisers or data brokers.</li>
              <li>We do not use your receipts or messages to train third-party AI models. AI helpers (transcription, receipt parsing, summaries) are called live per request and results are stored in your account only.</li>
            </ul>
          </div>

          <div className="space-y-2">
            <h2 className="font-heading text-lg font-extrabold text-white">
              Who processes your data
            </h2>
            <p>
              We use a small number of trusted providers to make the app work:
              a database (to store your entries), object storage (to hold your
              receipt photos), and AI providers (Claude for text, Gemini for
              image OCR, OpenAI Whisper for voice-to-text). These providers
              only see the exact request they need — nothing more.
            </p>
          </div>

          <div className="space-y-2">
            <h2 className="font-heading text-lg font-extrabold text-white">
              Deleting your account
            </h2>
            <p>
              You can permanently delete your account and every entry, job,
              category, invoice and file attached to it at any time from{" "}
              <Link
                to="/settings"
                data-testid="privacy-settings-link"
                className="text-[#FF9500] hover:underline"
              >
                Settings → Danger zone → Delete my account &amp; all data
              </Link>
              . The delete is immediate and irreversible — we don&apos;t hold
              a soft-copy in case you change your mind.
            </p>
          </div>

          <div className="space-y-2">
            <h2 className="font-heading text-lg font-extrabold text-white">
              A helper tool, not a tax agent
            </h2>
            <p>
              BuildIntel AI helps you capture and organise information. It does
              <b> not</b> lodge BAS or tax returns for you, and it is not a
              registered tax agent. Every number you see is an estimate — always
              double-check with your accountant before submitting anything to
              the ATO.
            </p>
          </div>

          <div className="space-y-2">
            <h2 className="font-heading text-lg font-extrabold text-white">
              Questions
            </h2>
            <p>
              If anything here is unclear, or you&apos;d like a copy of your
              data before deleting it, drop a note through the in-app chat and
              we&apos;ll come back to you.
            </p>
          </div>
        </section>

        <div className="flex flex-wrap gap-2 pt-2">
          {user ? (
            <Link
              to="/settings"
              data-testid="privacy-goto-settings"
              className="h-11 px-4 rounded-xl bi-ghost-btn inline-flex items-center gap-2 text-sm"
            >
              Back to Settings
            </Link>
          ) : (
            <Link
              to="/login"
              data-testid="privacy-goto-login"
              className="h-11 px-4 rounded-xl bi-accent-btn inline-flex items-center gap-2 text-sm font-heading font-bold uppercase tracking-wider"
            >
              Back to sign in
            </Link>
          )}
        </div>

        <PublicTrustFooter testId="privacy-trust-footer" />
      </div>
    </div>
  );
}
